<template>
	<section class="main-content contentblocks">
		<div
			v-for="block in contentblocks"
			:key="block.ID"
			class="contentblock"
			:class="[
				block.darkBackground ? 'dark-background' : '',
				block.mediumBackground ? 'medium-background' : '',
				block.image ? 'has-image' : '',
			]"
		>
			<div v-if="block.image" class="contentblock-image">
				<div class="parallax-container">
					<picture>
						<source
							v-if="block.imagePortraitWebp"
							media="(max-width: 880px)"
							:srcset="block.imagePortraitWebp"
							type="image/webp"
						/>
						<source media="(max-width: 880px)" :srcset="block.imagePortrait" />
						<source v-if="block.imageWebp" :srcset="block.imageWebp" type="image/webp" />
						<source :srcset="block.image" />

						<img
							class="block-image parallax-delay"
							loading="lazy"
							:src="block.image"
							:alt="block.imageAlt"
						/>
					</picture>
				</div>
			</div>
			<div class="row">
				<div :id="block.header.trim()" class="block-anchor" />
				<div class="row contentblock-content">
					<div class="columns column12">
						<picture v-if="block.graphicTop" class="intro-graphic top">
							<source :srcset="block.graphicTopWebp" type="image/webp" />
							<source :srcset="block.graphicTop" />
							<img :src="block.graphicTop" :alt="block.graphicTopAlt" />
						</picture>
						<h2>{{ block.header }}</h2>
						<div v-parse-links v-html="block.content" />
						<picture v-if="block.graphicBottom" class="intro-graphic bottom">
							<source :srcset="block.graphicBottomWebp" type="image/webp" />
							<source :srcset="block.graphicBottom" />
							<img :src="block.graphicBottom" :alt="block.graphicBottomAlt" />
						</picture>

						<frequently-asked-questions
							v-if="block.frequentlyAskedQuestion"
							:items="block.frequentlyAskedQuestion.faqitems"
							:button="block.frequentlyAskedQuestion.button"
							:link="block.frequentlyAskedQuestion.link"
						/>

						<p v-if="block.button" class="align-center margin-top">
							<a :href="block.buttonLink" target="_blank" class="button-no-fill">
								{{ block.button }}
							</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import Rellax from 'rellax';

defineProps({
	contentblocks: { type: Array, default: () => [] },
});

onMounted(() => {
	const speed = -5;
	new Rellax('.block-image', {
		speed,
		center: true,
		wrapper: null,
		round: true,
		vertical: true,
		horizontal: false,
	});
});
</script>

<style lang="scss" scoped>
.contentblocks {
	background: var(--primary-background-color);
	padding: 0;
}

.contentblock {
	margin: 0 auto;
	padding: 120px 0;
	position: relative;

	.margin-top {
		margin-top: 80px;
	}

	&.medium-background {
		background: var(--alternating-background-color);

		.contentblock-image {
			margin: 0 0 120px;
		}

		h2,
		p,
		:deep(p) {
			color: var(--subtitle-color);
		}

		.button-no-fill,
		:deep(.button-no-fill) {
			border-color: var(--subtitle-color);
			color: var(--subtitle-color);

			&:hover {
				border-color: var(--subtitle-color);
				background: var(--subtitle-color);
				color: #fff;
			}
		}

		.button,
		:deep(.button) {
			border-color: var(--subtitle-color);
			color: var(--subtitle-color);

			&:hover {
				border-color: var(--subtitle-color);
				background: none;
				color: var(--subtitle-color);
			}
		}
	}

	&.dark-background {
		background: var(--dark-background-color);

		.contentblock-image {
			margin: 0 0 120px;
		}

		h2,
		p,
		:deep(p) {
			color: #e9e2db;
		}

		.button-no-fill,
		:deep(.button-no-fill),
		.button,
		:deep(.button) {
			border-color: #e9e2db;
			color: #e9e2db;

			&:hover {
				border-color: var(--cta-color-hover);
			}
		}
	}

	&.has-image {
		padding-top: 0;
	}

	.intro-graphic {
		margin: 0 auto 40px;
		width: 90px;
		display: flex;

		&.bottom {
			width: 160px;
			margin: 20px auto 0;
		}
	}
}

.block-anchor {
	width: 1px;
	height: 1px;
	margin-top: -190px;
}

.contentblock-image {
	width: 100%;
	height: 100vh;
	margin: 0 0 120px;
	overflow: hidden;
	position: relative;

	.parallax-container {
		position: absolute;
		inset: 0;
		overflow: hidden;
	}

	img {
		width: 100%;
		height: 120%;
		margin-top: -5%;
		object-fit: cover;
	}
}

.contentblock-content {
	max-width: 670px;

	h2 {
		text-align: center;
	}
}

@media (max-width: 980px) {
	.contentblocks {
		padding: 0;
	}

	.contentblock-image {
		img {
			height: 140%;
			margin-top: -20%;
		}
	}
}
</style>
